import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Loader2, Link2, Unlink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const post = async (path, body) => {
  const r = await fetch(`${API}/api/gst/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || "Request failed");
  return d;
};

function ExpenseRow({ e, onLink, busy }) {
  return (
    <div className="flex items-center gap-2 border rounded p-2 text-xs" data-testid={`gst-cand-${e.expense_id}`}>
      <div className="flex-1">
        <b>{e.date}</b> · {e.expense_type} · {e.description || "—"} {e.mentions_period && <Badge className="ml-1 bg-green-100 text-green-800">names period</Badge>}
        {e.linked_to && <span className="ml-2 text-amber-700">already linked to {e.linked_to}</span>}
      </div>
      <b>₹{Number(e.amount).toLocaleString("en-IN")}</b>
      {!e.linked_to && <Button size="sm" onClick={() => onLink(e)} disabled={busy} data-testid={`gst-link-${e.expense_id}`}><Link2 className="w-3 h-3 mr-1" />Link</Button>}
    </div>
  );
}

export default function GstLinkPaymentDialog({ token, row, onClose, onChanged, fmt }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ca, setCa] = useState("");
  const [challan, setChallan] = useState("");
  const [rec, setRec] = useState({ date: "", amount: "", document_url: "" });
  const key = useRef(`${Date.now()}${Math.random().toString(16).slice(2)}`);
  const load = () => post("payment-candidates", { token, center: row.center, month: row.month }).then((x) => { setD(x); setCa(x.ca_liability ?? ""); }).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); await load(); onChanged(); } catch (e) { toast.error(e.message); } setBusy(false); };
  const link = (e) => act(() => post("link-payment", { token, center: row.center, month: row.month, expense_id: e.expense_id, challan_ref: challan || null }), "Existing expense linked — no new expense created");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="gst-link-dialog">
        <DialogHeader>
          <DialogTitle>GST {row.month} · {row.center}</DialogTitle>
          <DialogDescription>Calculated GST {fmt(row.gst_amount)} is a reference only. Actual paid = linked existing expenses.</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-end gap-2">
              <div><Label className="text-xs">CA-confirmed liability (₹)</Label><Input className="w-40 h-8" type="number" min="0" value={ca} onChange={(e) => setCa(e.target.value)} data-testid="gst-ca-liability" /></div>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => post("ca-liability", { token, center: row.center, month: row.month, ca_liability: ca }), "CA liability saved")} data-testid="gst-ca-save">Save CA liability</Button>
              <div><Label className="text-xs">Challan / reference (for next link)</Label><Input className="w-48 h-8" value={challan} onChange={(e) => setChallan(e.target.value)} data-testid="gst-challan" /></div>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Linked payments</h4>
              {d.links.length ? d.links.map((l) => (
                <div key={l.link_id} className="flex items-center gap-2 text-xs border rounded p-2 bg-green-50" data-testid={`gst-linked-${l.expense_id}`}>
                  <span className="flex-1">Paid {l.payment_date} · ₹{Number(l.amount).toLocaleString("en-IN")} · expense {l.expense_id}{l.challan_ref ? ` · ${l.challan_ref}` : ""} · {l.method} by {l.linked_by}</span>
                  <Button size="sm" variant="ghost" className="text-red-700" disabled={busy} onClick={() => act(() => post("unlink-payment", { token, link_id: l.link_id }), "Unlinked — the expense itself is unchanged")} data-testid={`gst-unlink-${l.expense_id}`}><Unlink className="w-3 h-3 mr-1" />Unlink</Button>
                </div>
              )) : <p className="text-xs text-muted-foreground">No payment linked yet.</p>}
            </div>
            <div>
              <h4 className="font-semibold mb-1">GST Payment Found in Expenses ({d.search_from} → before {d.search_to_excl})</h4>
              <div className="space-y-1">{d.candidates.length ? d.candidates.map((e) => <ExpenseRow key={e.expense_id} e={e} onLink={link} busy={busy} />) : <p className="text-xs text-muted-foreground">No GST expense in this window.</p>}</div>
            </div>
            {d.other_gst_expenses.length > 0 && (
              <details><summary className="cursor-pointer text-xs font-semibold">Other GST expenses for this centre (late/early payments)</summary>
                <div className="space-y-1 mt-1">{d.other_gst_expenses.map((e) => <ExpenseRow key={e.expense_id} e={e} onLink={link} busy={busy} />)}</div>
              </details>
            )}
            <div className="border-t pt-3">
              <h4 className="font-semibold mb-1">No matching expense? Record the actual payment once</h4>
              <div className="flex flex-wrap gap-2 items-end">
                <div><Label className="text-xs">Payment date</Label><Input type="date" className="h-8" value={rec.date} onChange={(e) => setRec({ ...rec, date: e.target.value })} data-testid="gst-rec-date" /></div>
                <div><Label className="text-xs">Amount paid (₹)</Label><Input type="number" min="0" className="h-8 w-32" value={rec.amount} onChange={(e) => setRec({ ...rec, amount: e.target.value })} data-testid="gst-rec-amount" /></div>
                <div><Label className="text-xs">Receipt URL (optional)</Label><Input className="h-8 w-56" value={rec.document_url} onChange={(e) => setRec({ ...rec, document_url: e.target.value })} /></div>
                <Button size="sm" disabled={busy || !rec.date || !rec.amount} data-testid="gst-rec-save"
                  onClick={() => act(() => post("record-payment", { token, center: row.center, month: row.month, ...rec, challan_ref: challan || null, idempotency_key: key.current }), "GST PAID expense created once and linked")}>Record & link</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Creates one “GST PAID” expense dated on the payment date (normal Expenses), then links it to {row.month}.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
